import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { formatCHF } from "@/lib/api";
import { AdminStripeEtat } from "@/components/AdminStripeEtat";
import { API, authHeaders, Badge, btn, btnPrimary, d, dt, errMsg, inputCls, Loading, PageHeader, Pager, statutTone, Table, useAdminGet } from "@/components/admin/ui";

export const Commandes = () => {
  const [data, reload] = useAdminGet("/admin/overview");
  const [busy, setBusy] = useState(false);
  const act = async (id, action, confirm) => {
    if (confirm && !window.confirm(confirm)) return;
    setBusy(true);
    try { await axios.post(`${API}/admin/orders/${id}`, { action }, authHeaders()); toast.success("Commande mise à jour"); await reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-commandes">
      <PageHeader title="Commandes" />
      <Table testid="admin-orders" cols={["N°", "Client", "Articles", "Total", "Paiement", "Statut", "Actions"]} empty="Aucune commande pour l'instant."
        rows={data.orders.map((o) => [
          <span className="font-mono text-xs">{o.numero}<br /><span className="text-fumee">{d(o.cree_le)}</span></span>,
          <span>{o.client.nom}<br /><span className="text-fumee text-xs">{o.client.email}<br />{o.client.adresse}, {o.client.npa} {o.client.ville}</span></span>,
          <span className="text-xs">{o.lignes.map((l) => `${l.nom} (${l.numero})`).join(", ")}</span>,
          <span>{formatCHF(o.total)}{o.remise_cadeau ? <><br /><span className="text-xs text-fumee">carte cadeau − {formatCHF(o.remise_cadeau)}</span></> : null}</span>,
          o.paiement === "facture" ? <Badge tone={o.facture_payee ? "ok" : "warn"}>{o.facture_payee ? "Facture payée" : "Facture"}</Badge> : <Badge tone="ok">{o.paiement === "carte_cadeau" ? "Carte cadeau" : "En ligne"}</Badge>,
          <span><Badge tone={statutTone(o.statut)}>{o.statut}</Badge>{o.expediee && <><br /><Badge tone="ok">Expédiée</Badge></>}</span>,
          <span className="flex flex-wrap gap-1">
            {o.paiement === "facture" && o.statut === "confirmée" && !o.facture_payee && <button disabled={busy} onClick={() => act(o.id, "facture_payee")} className={btn}>Facture payée</button>}
            {o.statut === "confirmée" && !o.expediee && <button disabled={busy} onClick={() => act(o.id, "expediee")} className={btn}>Expédiée</button>}
            {!["annulée", "expirée"].includes(o.statut) && <button disabled={busy} onClick={() => act(o.id, "annuler", "Annuler cette commande ?")} className={btn}>Annuler</button>}
          </span>,
        ])} />
    </div>
  );
};

export const TransactionsStripe = () => {
  const [f, setF] = useState({ statut: "", page: 1 });
  const [data] = useAdminGet("/admin/hub/paiements", f);
  return (
    <div data-testid="admin-stripe">
      <PageHeader title="Transactions Stripe" actions={<select value={f.statut} onChange={(e) => setF({ statut: e.target.value, page: 1 })} className={inputCls} data-testid="admin-stripe-filter"><option value="">Tous</option><option value="paid">Payées</option><option value="unpaid">Non payées</option><option value="expired">Expirées</option></select>} />
      <div className="mb-5"><AdminStripeEtat /></div>
      {!data ? <Loading /> : <>
        <Table testid="admin-stripe-table" cols={["Date", "Référence", "E-mail", "Objet", "Montant", "Statut"]} empty="Aucune transaction."
          rows={data.transactions.map((t) => [dt(t.created_at), <span className="font-mono text-xs">{t.reference}</span>, <span className="text-xs">{t.email}</span>, t.kind, formatCHF(t.amount), <Badge tone={t.payment_status === "paid" ? "ok" : "warn"}>{t.payment_status}</Badge>])} />
        <Pager page={data.page} pages={data.pages} total={data.total} onPage={(page) => setF((c) => ({ ...c, page }))} testid="admin-stripe-pager" />
      </>}
    </div>
  );
};

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
export const Exports = () => {
  const [periode, setPeriode] = useState("tout");
  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [mois, setMois] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [busy, setBusy] = useState(false);
  const exporter = async () => {
    setBusy(true);
    try {
      let params = {}; let suffixe = new Date().toISOString().slice(0, 10);
      if (periode === "annee") { params = { debut: `${annee}-01-01`, fin: `${annee}-12-31` }; suffixe = annee; }
      if (periode === "mois") { const fin = new Date(Number(annee), Number(mois), 0).getDate(); params = { debut: `${annee}-${mois}-01`, fin: `${annee}-${mois}-${fin}` }; suffixe = `${annee}-${mois}`; }
      const r = await axios.get(`${API}/admin/export/paiements.csv`, { ...authHeaders(), params, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `entrelesmondes-paiements-${suffixe}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-exports">
      <PageHeader title="Exports comptables" />
      <div className="bg-card border border-sand-dark rounded-xl p-5 flex flex-wrap items-end gap-3 max-w-2xl">
        <label className="text-xs text-fumee">Période<select value={periode} onChange={(e) => setPeriode(e.target.value)} className={`${inputCls} mt-1`} data-testid="admin-export-type"><option value="tout">Tout</option><option value="annee">Année</option><option value="mois">Mois</option></select></label>
        {periode !== "tout" && <label className="text-xs text-fumee">Année<select value={annee} onChange={(e) => setAnnee(e.target.value)} className={`${inputCls} mt-1`} data-testid="admin-export-annee">{[2025, 2026, 2027, 2028].map((y) => <option key={y}>{y}</option>)}</select></label>}
        {periode === "mois" && <label className="text-xs text-fumee">Mois<select value={mois} onChange={(e) => setMois(e.target.value)} className={`${inputCls} mt-1`} data-testid="admin-export-mois">{MOIS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}</select></label>}
        <button type="button" onClick={exporter} disabled={busy} className={btnPrimary} data-testid="admin-export-csv"><Download className="w-3.5 h-3.5" /> Télécharger le CSV</button>
      </div>
    </div>
  );
};
